import BaseService from './BaseService'

class ChannelService extends BaseService {
  constructor() {
    super('channels');
  }

  /**
   * Initialize the channels collection with sample data if it doesn't exist
   */
  async initializeChannelsCollection() {
    try {
      console.log('🔍 Checking if channels collection exists...')
      
      // Try to get all channels to see if collection exists
      const existingChannels = await this.getAllChannels({}, false) // Don't use cache
      
      if (existingChannels.length === 0) {
        console.log('📝 Channels collection is empty, creating sample channels...')
        await this.createSampleChannels()
        console.log('✅ Sample channels created successfully')
      } else {
        console.log(`✅ Channels collection exists with ${existingChannels.length} channels`)
      }
      
      return true
    } catch (error) {
      console.error('❌ Error initializing channels collection:', error)
      // If there's an error, try to create sample channels anyway
      try {
        console.log('🔄 Attempting to create sample channels despite error...')
        await this.createSampleChannels()
        console.log('✅ Sample channels created successfully')
        return true
      } catch (createError) {
        console.error('❌ Failed to create sample channels:', createError)
        throw createError
      }
    }
  }

  /**
   * Create sample channels for testing and demonstration
   */
  async createSampleChannels() {
    const sampleChannels = [
      {
        name: 'News Channel',
        description: '24/7 news coverage with latest updates',
        number: 1,
        url: 'https://example.com/news-stream.m3u8',
        thumbnail: 'https://images.unsplash.com/photo-1504711434969-e33886168f5c?w=400&h=225&fit=crop',
        isActive: true,
        isMuted: false,
        isPremium: false
      },
      {
        name: 'Sports Network',
        description: 'Live sports coverage and highlights',
        number: 2,
        url: 'https://example.com/sports-stream.m3u8',
        thumbnail: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=225&fit=crop',
        isActive: true,
        isMuted: false,
        isPremium: true
      },
      {
        name: 'Weather Channel',
        description: 'Real-time weather updates and forecasts',
        number: 3,
        url: 'https://example.com/weather-stream.m3u8',
        thumbnail: 'https://images.unsplash.com/photo-1504608524841-42fe6f032b4b?w=400&h=225&fit=crop',
        isActive: true,
        isMuted: true,
        isPremium: false
      },
      {
        name: 'Music Channel',
        description: 'Non-stop music videos and entertainment',
        number: 4,
        url: 'https://example.com/music-stream.m3u8',
        thumbnail: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=225&fit=crop',
        isActive: false,
        isMuted: false,
        isPremium: false
      },
      {
        name: 'Business News',
        description: 'Financial markets and business updates',
        number: 5,
        url: 'https://example.com/business-stream.m3u8',
        thumbnail: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=225&fit=crop',
        isActive: true,
        isMuted: false,
        isPremium: true
      }
    ]

    const creationPromises = sampleChannels.map(channel => 
      this.createChannel(channel)
    )

    await Promise.all(creationPromises)
    console.log(`✅ Created ${sampleChannels.length} sample channels`)
  }

  /**
   * Get all channels with optional filtering
   */
  async getAllChannels(filters = {}, useCache = true) {
    try {
      const channels = await this.getDocuments(filters, { field: 'number', direction: 'asc' }, null, useCache);
      return channels;
    } catch (error) {
      // If it's a permission error, the collection might not exist yet
      if (error.code === 'permission-denied' || error.message.includes('Missing or insufficient permissions')) {
        console.log('📝 Channels collection does not exist yet, returning empty array');
        return [];
      }
      console.error('Error loading channels:', error);
      throw error;
    }
  }

  /**
   * Get active channels only
   */
  async getActiveChannels(useCache = true) {
    try {
      const channels = await this.getDocuments({ isActive: true }, { field: 'number', direction: 'asc' }, null, useCache);
      return channels;
    } catch (error) {
      console.error('Error loading active channels:', error);
      throw error;
    }
  }

  /**
   * Get a specific channel by ID
   */
  async getChannel(channelId, useCache = true) {
    try {
      const channel = await this.getDocument(channelId, useCache);
      return channel;
    } catch (error) {
      console.error('Error loading channel:', error);
      throw error;
    }
  }

  /**
   * Get channel by number
   */
  async getChannelByNumber(number, useCache = true) {
    try {
      const channels = await this.getDocuments({ number: number }, null, 1, useCache);
      return channels.length > 0 ? channels[0] : null;
    } catch (error) {
      console.error('Error loading channel by number:', error);
      throw error;
    }
  }

  /**
   * Create a new channel
   */
  async createChannel(channelData) {
    try {
      // Validate required fields
      if (!channelData.name || !channelData.url) {
        throw new Error('Channel name and URL are required');
      }

      // Set default values
      const data = {
        ...channelData,
        isActive: channelData.isActive !== undefined ? channelData.isActive : true,
        isMuted: channelData.isMuted !== undefined ? channelData.isMuted : false,
        isPremium: channelData.isPremium !== undefined ? channelData.isPremium : false,
        hits: channelData.hits || 0,
        created: new Date().getTime(),
        modified: new Date().getTime()
      };

      const result = await this.createDocument(data);
      return result;
    } catch (error) {
      console.error('Error creating channel:', error);
      throw error;
    }
  }

  /**
   * Update an existing channel
   */
  async updateChannel(channelId, channelData) {
    try {
      // Validate required fields
      if (!channelData.name || !channelData.url) {
        throw new Error('Channel name and URL are required');
      }

      const data = {
        ...channelData,
        modified: new Date().getTime()
      };

      const result = await this.updateDocument(channelId, data);
      return result;
    } catch (error) {
      console.error('Error updating channel:', error);
      throw error;
    }
  }

  /**
   * Delete a channel
   */
  async deleteChannel(channelId) {
    try {
      const result = await this.deleteDocument(channelId);
      return result;
    } catch (error) {
      console.error('Error deleting channel:', error);
      throw error;
    }
  }

  /**
   * Toggle channel active status
   */
  async toggleChannelActive(channelId, isActive) {
    try {
      const result = await this.updateDocument(channelId, { 
        isActive: isActive,
        modified: new Date().getTime()
      });
      return result;
    } catch (error) {
      console.error('Error toggling channel active status:', error);
      throw error;
    }
  }

  /**
   * Toggle channel mute status
   */
  async toggleChannelMute(channelId, isMuted) {
    try {
      const result = await this.updateDocument(channelId, { 
        isMuted: isMuted,
        modified: new Date().getTime()
      });
      return result;
    } catch (error) {
      console.error('Error toggling channel mute status:', error);
      throw error;
    }
  }

  /**
   * Increment channel hits
   */
  async incrementHits(channelId) {
    try {
      const channel = await this.getChannel(channelId, false);
      if (channel) {
        const newHits = (channel.hits || 0) + 1;
        const result = await this.updateDocument(channelId, { 
          hits: newHits,
          modified: new Date().getTime()
        });
        return result;
      }
    } catch (error) {
      console.error('Error incrementing channel hits:', error);
      throw error;
    }
  }

  /**
   * Get channels by premium status
   */
  async getChannelsByPremiumStatus(isPremium, useCache = true) {
    try {
      const channels = await this.getDocuments({ isPremium: isPremium }, { field: 'number', direction: 'asc' }, null, useCache);
      return channels;
    } catch (error) {
      console.error('Error loading channels by premium status:', error);
      throw error;
    }
  }

  /**
   * Search channels by name or description
   */
  async searchChannels(searchTerm, useCache = true) {
    try {
      // Get all channels and filter client-side since Firestore doesn't support full-text search
      const allChannels = await this.getAllChannels({}, useCache);
      const searchLower = searchTerm.toLowerCase();
      
      return allChannels.filter(channel => 
        channel.name?.toLowerCase().includes(searchLower) ||
        channel.description?.toLowerCase().includes(searchLower)
      );
    } catch (error) {
      console.error('Error searching channels:', error);
      throw error;
    }
  }

  /**
   * Get channels statistics
   */
  async getChannelStats() {
    try {
      const allChannels = await this.getAllChannels({}, false);
      
      const stats = {
        total: allChannels.length,
        active: allChannels.filter(ch => ch.isActive).length,
        inactive: allChannels.filter(ch => !ch.isActive).length,
        premium: allChannels.filter(ch => ch.isPremium).length,
        regular: allChannels.filter(ch => !ch.isPremium).length,
        muted: allChannels.filter(ch => ch.isMuted).length,
        totalHits: allChannels.reduce((sum, ch) => sum + (ch.hits || 0), 0),
        averageHits: allChannels.length > 0 ? 
          Math.round(allChannels.reduce((sum, ch) => sum + (ch.hits || 0), 0) / allChannels.length) : 0
      };
      
      return stats;
    } catch (error) {
      console.error('Error getting channel statistics:', error);
      throw error;
    }
  }

  /**
   * Subscribe to real-time channel updates
   */
  subscribeToChannels(callback, filters = {}) {
    return this.subscribeToDocuments(filters, callback, { 
      sortBy: { field: 'number', direction: 'asc' } 
    });
  }

  /**
   * Subscribe to real-time updates for a single channel
   */
  subscribeToChannel(channelId, callback) {
    return this.subscribeToDocument(channelId, callback);
  }
}

// Create singleton instance
const channelService = new ChannelService();
export { ChannelService };
export default channelService; 